class HiyoContentCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hiyo-carousel-track]');
    this.items = [...this.querySelectorAll('[data-hiyo-carousel-item]')];
    this.previousButton = this.querySelector('[data-hiyo-carousel-previous]');
    this.nextButton = this.querySelector('[data-hiyo-carousel-next]');
    this.progressThumb = this.querySelector('[data-hiyo-carousel-progress]');
    this.abortController = new AbortController();

    if (!this.track || this.items.length === 0) return;

    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.scrollByItem(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.scrollByItem(1), { signal });
    this.track.addEventListener('scroll', this.queueUpdate, { passive: true, signal });
    this.track.addEventListener('keydown', this.handleTrackKeydown, { signal });
    this.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

    this.resizeObserver = new ResizeObserver(this.queueUpdate);
    this.resizeObserver.observe(this.track);

    this.updateState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
  }

  queueUpdate = () => {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateState());
  };

  getStep() {
    if (!this.track || this.items.length === 0) return 0;

    const itemWidth = this.items[0].getBoundingClientRect().width;
    const styles = getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

    return itemWidth + gap;
  }

  isRtl() {
    return this.track && getComputedStyle(this.track).direction === 'rtl';
  }

  getNormalizedScrollLeft() {
    if (!this.track) return 0;

    const rawScrollLeft = this.track.scrollLeft;
    return Math.max(0, this.isRtl() ? -rawScrollLeft : rawScrollLeft);
  }

  scrollByItem(direction) {
    if (!this.track) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: this.getStep() * direction * (this.isRtl() ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  handleTrackKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const physicalDirection = event.key === 'ArrowLeft' ? -1 : 1;
    this.scrollByItem(this.isRtl() ? -physicalDirection : physicalDirection);
  };

  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-hiyo-carousel-item]');
    if (!item) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  updateState() {
    if (!this.track || this.items.length === 0) return;

    const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const currentScrollLeft = this.getNormalizedScrollLeft();
    const edgeTolerance = 2;
    const scrollProgress = maxScrollLeft > 0 ? currentScrollLeft / maxScrollLeft : 0;

    if (this.previousButton) {
      this.previousButton.disabled = currentScrollLeft <= edgeTolerance;
    }

    if (this.nextButton) {
      this.nextButton.disabled = currentScrollLeft >= maxScrollLeft - edgeTolerance;
    }

    if (this.progressThumb) {
      const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const remainingRatio = 1 - visibleRatio;
      this.progressThumb.style.width = `${visibleRatio * 100}%`;
      this.progressThumb.style.insetInlineStart = `${scrollProgress * remainingRatio * 100}%`;
    }

    const step = this.getStep();
    const selectedIndex = step > 0 ? Math.round(currentScrollLeft / step) : 0;
    const selectedItem = this.items[Math.min(this.items.length - 1, Math.max(0, selectedIndex))];

    this.items.forEach((item) => {
      item.classList.toggle('is-selected', item === selectedItem);
    });
  }
}

if (!customElements.get('hiyo-content-carousel')) {
  customElements.define('hiyo-content-carousel', HiyoContentCarousel);
}
